import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Upload, Users, Instagram, Facebook, Twitter, Edit, UserCog, Search, UserPlus, Trash2, Shield, MessageSquare, Send } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import ActiveSquadSheet from "@/components/ActiveSquadSheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Standard team roles
const STANDARD_ROLES = [
  { value: "captain", label: "Captain" },
  { value: "vice_captain", label: "Vice Captain" },
  { value: "social_secretary", label: "Social Secretary" },
  { value: "team_admin", label: "Team Admin" },
  { value: "treasurer", label: "Treasurer" },
];

export default function TeamDetails({ user, logout }) {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showActiveSheet, setShowActiveSheet] = useState(false);
  const [joinRequests, setJoinRequests] = useState([]);
  const [allMembers, setAllMembers] = useState([]); // All authorized users for captain selection
  
  // Team roles
  const [teamRoles, setTeamRoles] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [selectedRoleType, setSelectedRoleType] = useState("captain");
  const [selectedRoleUserId, setSelectedRoleUserId] = useState("");
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [assigningRole, setAssigningRole] = useState(false);
  
  // Add member form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [membershipNumber, setMembershipNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [memberType, setMemberType] = useState("non-section");
  const [adding, setAdding] = useState(false);
  
  // Bulk upload
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Social media
  const [showSocialDialog, setShowSocialDialog] = useState(false);
  const [instagram, setInstagram] = useState("");
  const [facebook, setFacebook] = useState("");
  const [twitter, setTwitter] = useState("");
  const [savingSocial, setSavingSocial] = useState(false);
  
  // Broadcast to team
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);
  
  // Team settings
  const [teamPrioritySignup, setTeamPrioritySignup] = useState(false);
  const [captainApprovalRequired, setCaptainApprovalRequired] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Captain assignment (legacy - keeping for backward compatibility)
  const [showCaptainDialog, setShowCaptainDialog] = useState(false);
  const [captainRole, setCaptainRole] = useState("team_captain");
  const [selectedCaptainId, setSelectedCaptainId] = useState("");
  const [assigningCaptain, setAssigningCaptain] = useState(false);
  const [captainSearchTerm, setCaptainSearchTerm] = useState("");
  
  // Add player to team
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [playerSearchTerm, setPlayerSearchTerm] = useState("");
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [teamRole, setTeamRole] = useState("primary"); // primary or secondary

  // Check permissions - include team_roles for users with roles in specific teams
  const adminRoles = ["convenor", "captain"];
  const teamManagementRoles = ["captain", "team_captain", "vice_captain", "manager", "team_admin"];
  
  const isAdmin = adminRoles.includes(user.role);
  
  // Check if user has a management role for THIS specific team via team_roles
  const hasTeamRoleForThisTeam = (user.team_roles || []).some(tr => 
    tr.team_id === teamId && teamManagementRoles.includes(tr.role)
  );
  
  const isTeamCaptain = (["team_captain", "vice_captain"].includes(user.role) && user.team_id === teamId) || hasTeamRoleForThisTeam;
  const canManage = isAdmin || isTeamCaptain;
  
  // PDPA: Only convenor, section captain, and team captains can see sensitive info
  const canViewSensitiveInfo = ["convenor", "captain", "team_captain", "vice_captain", "admin"].includes(user.role) || hasTeamRoleForThisTeam;

  useEffect(() => {
    fetchTeamDetails();
    if (canManage) {
      fetchJoinRequests();
    }
    if (isAdmin) {
      fetchAllMembers();
      fetchTeamRoles();
    }
  }, [teamId]);

  const fetchTeamRoles = async () => {
    try {
      const response = await axios.get(`${API}/teams/${teamId}/roles`, {
        withCredentials: true
      });
      setTeamRoles(response.data.roles || []);
      setCustomRoles(response.data.custom_roles || []);
    } catch (error) {
      console.error("Error fetching team roles:", error);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedRoleUserId || !selectedRoleType) {
      toast.error("Please select a member and role");
      return;
    }
    
    setAssigningRole(true);
    try {
      await axios.post(
        `${API}/teams/${teamId}/roles`,
        { user_id: selectedRoleUserId, role: selectedRoleType },
        { withCredentials: true }
      );
      toast.success("Role assigned successfully");
      setShowRoleDialog(false);
      setSelectedRoleUserId("");
      setRoleSearchTerm("");
      fetchTeamRoles();
      fetchTeamDetails();
    } catch (error) {
      console.error("Error assigning role:", error);
      toast.error(error.response?.data?.detail || "Failed to assign role");
    } finally {
      setAssigningRole(false);
    }
  };

  const handleRemoveRole = async (userId, role) => {
    if (!window.confirm("Remove this role assignment?")) return;
    
    try {
      await axios.delete(`${API}/teams/${teamId}/roles/${userId}/${role}`, {
        withCredentials: true
      });
      toast.success("Role removed");
      fetchTeamRoles();
      fetchTeamDetails();
    } catch (error) {
      console.error("Error removing role:", error);
      toast.error("Failed to remove role");
    }
  };

  const fetchTeamDetails = async () => {
    try {
      const response = await axios.get(`${API}/teams/${teamId}/details`, {
        withCredentials: true
      });
      setTeam(response.data);
      // Set social media fields
      setInstagram(response.data.instagram || "");
      setFacebook(response.data.facebook || "");
      setTwitter(response.data.twitter || "");
      // Set team settings
      setTeamPrioritySignup(response.data.team_priority_signup || false);
      setCaptainApprovalRequired(response.data.captain_approval_required || false);
    } catch (error) {
      console.error("Error fetching team details:", error);
      toast.error("Failed to load team details");
    } finally {
      setLoading(false);
    }
  };
  
  const openSocialDialog = () => {
    setInstagram(team.instagram || "");
    setFacebook(team.facebook || "");
    setTwitter(team.twitter || "");
    setShowSocialDialog(true);
  };
  
  const saveSocialMedia = async () => {
    setSavingSocial(true);
    try {
      await axios.put(
        `${API}/teams/${teamId}/social-media`,
        {
          instagram,
          facebook,
          twitter
        },
        { withCredentials: true }
      );
      toast.success("Social media links updated!");
      setShowSocialDialog(false);
      fetchTeamDetails();
    } catch (error) {
      console.error("Error updating social media:", error);
      toast.error("Failed to update social media");
    } finally {
      setSavingSocial(false);
    }
  };

  const toggleTeamPrioritySignup = async () => {
    setSavingSettings(true);
    try {
      const newValue = !teamPrioritySignup;
      await axios.put(
        `${API}/teams/${teamId}/settings`,
        { team_priority_signup: newValue },
        { withCredentials: true }
      );
      setTeamPrioritySignup(newValue);
      toast.success(newValue 
        ? "Team priority signup enabled - 1st team players get main list priority" 
        : "Team priority signup disabled - standard first-come-first-served");
    } catch (error) {
      console.error("Error updating team settings:", error);
      toast.error("Failed to update team settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleCaptainApproval = async () => {
    setSavingSettings(true);
    try {
      const newValue = !captainApprovalRequired;
      await axios.put(
        `${API}/teams/${teamId}/settings`,
        { captain_approval_required: newValue },
        { withCredentials: true }
      );
      setCaptainApprovalRequired(newValue);
      toast.success(newValue 
        ? "Captain approval ON - players must be approved before being added to squad" 
        : "Captain approval OFF - players can sign up directly");
    } catch (error) {
      console.error("Error updating team settings:", error);
      toast.error("Failed to update team settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Broadcast message to team members via Telegram
  const sendTeamBroadcast = async () => {
    if (!broadcastMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    setBroadcasting(true);
    try {
      const response = await axios.post(
        `${API}/admin/broadcast`,
        { 
          recipient_type: "section_team", 
          team_id: teamId,
          message: broadcastMessage,
          channel: "telegram"
        },
        { withCredentials: true }
      );
      
      toast.success(`Message sent to ${response.data.sent?.telegram || 0} team members via Telegram`);
      setShowBroadcastDialog(false);
      setBroadcastMessage("");
    } catch (error) {
      console.error("Error sending broadcast:", error);
      toast.error(error.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setBroadcasting(false);
    }
  };

  const fetchAllMembers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      // Filter to only authorized users
      setAllMembers(response.data.filter(u => u.is_authorized));
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const assignCaptain = async () => {
    if (!selectedCaptainId) {
      toast.error("Please select a member");
      return;
    }
    
    setAssigningCaptain(true);
    try {
      const response = await axios.post(
        `${API}/teams/${teamId}/assign-captain`,
        { user_id: selectedCaptainId, role: captainRole },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setShowCaptainDialog(false);
      setSelectedCaptainId("");
      fetchTeamDetails();
      fetchAllMembers(); // Refresh members list
    } catch (error) {
      console.error("Error assigning captain:", error);
      toast.error(error.response?.data?.detail || "Failed to assign captain");
    } finally {
      setAssigningCaptain(false);
    }
  };

  const removeCaptain = async (role) => {
    if (!window.confirm(`Remove ${role === 'team_captain' ? 'Captain' : 'Vice Captain'} from this team?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/teams/${teamId}/remove-captain?role=${role}`, { withCredentials: true });
      toast.success(`${role === 'team_captain' ? 'Captain' : 'Vice Captain'} removed`);
      fetchTeamDetails();
    } catch (error) {
      console.error("Error removing captain:", error);
      toast.error("Failed to remove captain");
    }
  };

  const addPlayerToTeam = async () => {
    if (!selectedPlayerId) {
      toast.error("Please select a player");
      return;
    }
    
    setAddingPlayer(true);
    try {
      await axios.post(
        `${API}/teams/${teamId}/add-player`,
        { user_id: selectedPlayerId, team_role: teamRole },
        { withCredentials: true }
      );
      toast.success(`Player added to team as ${teamRole === 'primary' ? '1st' : '2nd'} team`);
      setShowAddPlayerDialog(false);
      setSelectedPlayerId("");
      setPlayerSearchTerm("");
      fetchTeamDetails();
      fetchAllMembers();
    } catch (error) {
      console.error("Error adding player:", error);
      toast.error(error.response?.data?.detail || "Failed to add player to team");
    } finally {
      setAddingPlayer(false);
    }
  };

  const fetchJoinRequests = async () => {
    try {
      const response = await axios.get(`${API}/teams/${teamId}/join-requests`, {
        withCredentials: true
      });
      setJoinRequests(response.data);
    } catch (error) {
      console.error("Error fetching join requests:", error);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await axios.post(`${API}/teams/join-requests/${requestId}/approve`, {}, {
        withCredentials: true
      });
      toast.success("Join request approved!");
      fetchJoinRequests();
      fetchTeamDetails();
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error("Failed to approve request");
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axios.post(`${API}/teams/join-requests/${requestId}/reject`, {}, {
        withCredentials: true
      });
      toast.success("Join request rejected");
      fetchJoinRequests();
    } catch (error) {
      console.error("Error rejecting request:", error);
      toast.error("Failed to reject request");
    }
  };

  const addMember = async () => {
    if (!name || !email || !membershipNumber) {
      toast.error("Please fill all required fields");
      return;
    }

    setAdding(true);
    try {
      const response = await axios.post(
        `${API}/teams/add-member`,
        {
          name,
          email,
          membership_number: membershipNumber,
          team_id: teamId,
          phone: phone || null,
          member_type: memberType
        },
        { withCredentials: true }
      );

      toast.success(`Member added! Default password: ${response.data.default_password}`);
      setShowAddDialog(false);
      resetForm();
      fetchTeamDetails();
    } catch (error) {
      console.error("Error adding member:", error);
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setAdding(false);
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    setUploadResults(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API}/teams/bulk-upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true
        }
      );

      setUploadResults(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Successfully added ${response.data.success} members!`);
        fetchTeamDetails();
      }
      
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} members failed to add`);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const removeMember = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this member from the team?")) {
      return;
    }

    try {
      await axios.delete(`${API}/teams/${teamId}/members/${userId}`, {
        withCredentials: true
      });
      toast.success("Member removed from team");
      
      // Immediately update UI by removing member from team state
      setTeam(prevTeam => ({
        ...prevTeam,
        members: prevTeam.members.filter(m => m.user_id !== userId)
      }));
      
      // Also refresh from server to ensure consistency
      await fetchTeamDetails();
      await fetchJoinRequests();
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const downloadTemplate = () => {
    const csvContent = "Name,Email,Membership Number,Team,Phone,Member Type,Birthdate,Playing Position,Joined SCC Date\nJohn Doe,john@example.com,P0123,Legends,+65 1234 5678,section,1995-06-15,Midfielder,2020-01-01\nJane Smith,jane@example.com,P0456,Legends,+65 8765 4321,non-section,1992-03-22,Forward,2019-06-15";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'member_upload_template.csv';
    link.click();
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setMembershipNumber("");
    setPhone("");
    setMemberType("non-section");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <p>Team not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/teams">
            <Button variant="ghost" className="text-yellow-400 hover:text-yellow-300" data-testid="back-button">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Teams
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-yellow-400 uppercase" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif", letterSpacing: "0.05em" }}>SCC Football</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 md:py-12 max-w-full overflow-x-hidden">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
          <div className="min-w-0">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-2 break-words" data-testid="team-name">{team.name}</h2>
            <p className="text-slate-400">{team.members_details?.length || 0} members</p>
            
            {/* Social Media Links */}
            <div className="flex flex-wrap items-center gap-4 mt-3">
              {team.instagram && (
                <a 
                  href={team.instagram.startsWith('http') ? team.instagram : `https://instagram.com/${team.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-600 hover:text-pink-400 transition-colors"
                  title="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {team.facebook && (
                <a 
                  href={team.facebook.startsWith('http') ? team.facebook : `https://facebook.com/${team.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-400 transition-colors"
                  title="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {team.twitter && (
                <a 
                  href={team.twitter.startsWith('http') ? team.twitter : `https://twitter.com/${team.twitter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-500 hover:text-sky-600 transition-colors"
                  title="Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              )}
              {canManage && (
                <Button
                  onClick={openSocialDialog}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-slate-300"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">{team.instagram || team.facebook || team.twitter ? 'Edit' : 'Add'} Social Media</span>
                  <span className="sm:hidden">Edit</span>
                </Button>
              )}
            </div>
          </div>
          
          {canManage && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
              <Button
                onClick={() => setShowAddDialog(true)}
                className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold w-full sm:w-auto"
                data-testid="add-member-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Member
              </Button>
              <Button
                onClick={() => setShowUploadDialog(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold w-full sm:w-auto"
                data-testid="bulk-upload-button"
              >
                <Upload className="w-5 h-5 mr-2" />
                Bulk Upload
              </Button>
              <Button
                onClick={() => setShowActiveSheet(true)}
                variant="outline"
                className="font-semibold w-full sm:w-auto"
                data-testid="who-gets-messaged-button"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Who gets messaged
              </Button>
            </div>
          )}
        </div>

        {/* Team Settings Section (Only for Captains/Admins) */}
        {canManage && (
          <Card className="p-6 bg-blue-500/10 border-2 border-blue-500/30 shadow-lg mb-8">
            <h3 className="text-xl font-bold text-white mb-4">Team Settings</h3>
            <div className="space-y-4">
              
              {/* Team Roles Assignment (Admin only) */}
              {isAdmin && (
                <div className="p-4 bg-slate-900/50 rounded-lg border border-blue-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-white flex items-center gap-2">
                      <Shield className="w-5 h-5 text-yellow-500" />
                      Team Leadership & Roles
                    </p>
                    <Button 
                      onClick={() => setShowRoleDialog(true)}
                      className="bg-yellow-500/100 hover:bg-yellow-400 text-black"
                      size="sm"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Assign Role
                    </Button>
                  </div>
                  
                  {/* Current Role Assignments */}
                  <div className="space-y-2 mb-4">
                    {teamRoles.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">
                        No roles assigned yet. Click "Assign Role" to add team captains, vice captains, social secretary, etc.
                      </p>
                    ) : (
                      teamRoles.map((role, idx) => (
                        <div 
                          key={`${role.user_id}-${role.role}-${idx}`}
                          className={`flex items-center justify-between p-2 rounded ${
                            role.role === 'captain' ? 'bg-yellow-500/10' : 
                            role.role === 'vice_captain' ? 'bg-purple-500/10' : 
                            'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                              role.role === 'captain' ? 'bg-yellow-500/20 text-yellow-400' : 
                              role.role === 'vice_captain' ? 'bg-purple-500/20 text-purple-400' : 
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {role.title}
                            </span>
                            <span className="text-sm text-slate-300">{role.user_name}</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleRemoveRole(role.user_id, role.role)}
                            className="text-red-400 hover:bg-red-500/10 h-7 w-7 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <Button 
                    onClick={() => setShowAddPlayerDialog(true)}
                    className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
                    size="sm"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Player to Team
                  </Button>
                </div>
              )}
              
              {/* Broadcast to Team (Captain or Admin) */}
              {canManage && (
                <div className="p-4 bg-slate-900/50 rounded-lg border border-purple-500/30">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-white flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-purple-400" />
                        Broadcast to Team
                      </p>
                      <p className="text-sm text-slate-400">
                        Send a message to all team members via Telegram
                      </p>
                    </div>
                    <Button 
                      onClick={() => setShowBroadcastDialog(true)}
                      className="bg-purple-600 hover:bg-purple-700"
                      size="sm"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Team Priority Signup Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-blue-500/30">
                <div className="flex-1">
                  <p className="font-semibold text-white">Team Priority Signup</p>
                  <p className="text-sm text-slate-400">
                    When enabled, players who have this as their 1st team go to the main list. 
                    Players with this as their 2nd team go to standby list first.
                  </p>
                </div>
                <button
                  onClick={toggleTeamPrioritySignup}
                  disabled={savingSettings}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    teamPrioritySignup ? 'bg-blue-600' : 'bg-gray-300'
                  } ${savingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-slate-900/50 transition-transform ${
                      teamPrioritySignup ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {teamPrioritySignup && (
                <div className="p-3 bg-blue-500/20 rounded-lg text-sm text-blue-400">
                  <p className="font-medium">How it works:</p>
                  <ul className="list-disc ml-4 mt-1 space-y-1">
                    <li>1st team players → Main squad (if spots available)</li>
                    <li>2nd team players → Standby list</li>
                    <li>48 hours before game: Auto-promote standby if main list not full (when auto-promote enabled)</li>
                    <li>Players can't sign up for 2nd team game if already playing for 1st team same day</li>
                  </ul>
                </div>
              )}
              
              {/* Captain Approval Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-green-500/30 mt-4">
                <div className="flex-1">
                  <p className="font-semibold text-white">Captain Approval Required</p>
                  <p className="text-sm text-slate-400">
                    When enabled, players must wait for captain approval before being added to the squad.
                    You'll receive a notification for each signup request.
                  </p>
                </div>
                <button
                  onClick={toggleCaptainApproval}
                  disabled={savingSettings}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    captainApprovalRequired ? 'bg-green-600' : 'bg-gray-300'
                  } ${savingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-slate-900/50 transition-transform ${
                      captainApprovalRequired ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {captainApprovalRequired && (
                <div className="p-3 bg-emerald-500/20 rounded-lg text-sm text-emerald-400">
                  <p className="font-medium">How it works:</p>
                  <ul className="list-disc ml-4 mt-1 space-y-1">
                    <li>Players click "Sign Up" → Request goes to pending list</li>
                    <li>Captain gets notified of each signup request</li>
                    <li>Captain approves → Player added to Main Squad or Standby</li>
                    <li>Player gets notified of approval status</li>
                  </ul>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Join Requests Section (Only for Captains/Admins) */}
        {canManage && joinRequests.length > 0 && (
          <Card className="p-6 bg-yellow-500/10 border-2 border-yellow-500/30 shadow-lg mb-8">
            <h3 className="text-xl font-bold text-white mb-4">
              Pending Join Requests ({joinRequests.length})
            </h3>
            <div className="space-y-3">
              {joinRequests.map((request) => (
                <Card key={request.id} className="p-4 bg-slate-900/50 border border-yellow-500/30">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-white">{request.user_name}</p>
                      <p className="text-sm text-slate-400">{request.user_email}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Requested: {new Date(request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApproveRequest(request.id)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        size="sm"
                      >
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleRejectRequest(request.id)}
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        size="sm"
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Members List */}
        {team.members_details && team.members_details.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {team.members_details.map((member, index) => (
              <Card
                key={member.user_id}
                className="p-6 bg-slate-900/50 border-2 border-white/5 shadow-lg"
                data-testid={`member-card-${index}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center mr-3">
                      <Users className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white" data-testid={`member-name-${index}`}>{member.name}</h3>
                      <p className="text-xs text-slate-400">{member.role}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    member.member_type === 'section' 
                      ? 'bg-yellow-500/20 text-yellow-400' 
                      : 'bg-slate-800/50 text-white'
                  }`}>
                    {member.member_type === 'section' ? 'Section' : 'Non-Section'}
                  </span>
                </div>
                
                {/* Public Info - Always Visible */}
                <div className="space-y-2 text-sm mb-3">
                  {member.birthdate && (
                    <p className="text-slate-400">
                      <span className="font-semibold">Age:</span> {(() => {
                        const birthYear = new Date(member.birthdate).getFullYear();
                        const currentYear = new Date().getFullYear();
                        return currentYear - birthYear;
                      })()}
                    </p>
                  )}
                  {member.playing_position && (
                    <p className="text-slate-400">
                      <span className="font-semibold">Position:</span> {member.playing_position}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <p className="text-slate-400">
                      <span className="font-semibold">⚽ Goals:</span> {member.goals_scored || 0}
                    </p>
                    <p className="text-slate-400">
                      <span className="font-semibold">🅰️ Assists:</span> {member.assists || 0}
                    </p>
                    <p className="text-slate-400">
                      <span className="font-semibold">🏆 MOTM:</span> {member.motm_awards || 0}
                    </p>
                  </div>
                </div>
                
                {/* Sensitive Info - Only for Privileged Roles */}
                <div className="border-t pt-3 space-y-1 text-sm">
                  {canViewSensitiveInfo ? (
                    <>
                      <p className="text-slate-400">
                        <span className="font-semibold">Member #:</span> {member.membership_number}
                      </p>
                      <p className="text-slate-400">
                        <span className="font-semibold">Email:</span> {member.email}
                      </p>
                      {member.phone && member.phone !== 'N/A' && (
                        <p className="text-slate-400">
                          <span className="font-semibold">Phone:</span> {member.phone}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-slate-400 italic">
                      📞 Contact details visible to captains only
                    </p>
                  )}
                </div>
                
                {canManage && (
                  <Button
                    onClick={() => removeMember(member.user_id)}
                    variant="outline"
                    size="sm"
                    className="w-full mt-3 border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid={`remove-member-button-${index}`}
                  >
                    Remove from Team
                  </Button>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Members Yet</h3>
            <p className="text-slate-400 mb-6">Start by adding members to this team</p>
            {canManage && (
              <Button
                onClick={() => setShowAddDialog(true)}
                className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add First Member
              </Button>
            )}
          </Card>
        )}
      </div>

      {/* Add Member Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-member-dialog">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Full Name *</label>
              <Input
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Email *</label>
              <Input
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email-input"
              />
              <p className="text-xs text-slate-400 mt-1">This will be their login username</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Membership Number *</label>
              <Input
                placeholder="SCC-12345678"
                value={membershipNumber}
                onChange={(e) => setMembershipNumber(e.target.value)}
                data-testid="membership-number-input"
              />
              <p className="text-xs text-slate-400 mt-1">This will be their default password</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Phone (Optional)</label>
              <Input
                placeholder="+65 1234 5678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="phone-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Member Type</label>
              <div className="space-y-2" data-testid="member-type-select">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="memberType"
                    value="section"
                    checked={memberType === "section"}
                    onChange={(e) => setMemberType(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span>Section Member</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="memberType"
                    value="non-section"
                    checked={memberType === "non-section"}
                    onChange={(e) => setMemberType(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span>Non-Section Member</span>
                </label>
              </div>
            </div>
            
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded">
              <p className="text-xs text-blue-400">
                <strong>Login Credentials:</strong><br/>
                Username: {email || "[email]"}<br/>
                Password: {membershipNumber || "[membership number]"}<br/>
                User will be prompted to change password on first login.
              </p>
            </div>

            <Button
              onClick={addMember}
              disabled={adding}
              className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
              data-testid="submit-add-button"
            >
              {adding ? "Adding..." : "Add Member"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Members</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Upload CSV or Excel File</label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files[0])}
                data-testid="file-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                Required columns: Name, Email, Membership Number, Team<br/>
                Optional: Phone, Member Type
              </p>
            </div>

            <Button
              onClick={downloadTemplate}
              variant="outline"
              className="w-full"
              data-testid="download-template-button"
            >
              Download CSV Template
            </Button>

            {uploadResults && (
              <Card className="p-4 bg-[#0a0f1a]">
                <h4 className="font-semibold mb-2">Upload Results:</h4>
                <p className="text-sm text-emerald-400">✓ Success: {uploadResults.success}</p>
                <p className="text-sm text-red-400">✗ Failed: {uploadResults.failed}</p>
                {uploadResults.errors && uploadResults.errors.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto">
                    <p className="text-xs font-semibold mb-1">Errors:</p>
                    {uploadResults.errors.map((error, i) => (
                      <p key={i} className="text-xs text-red-400">{error}</p>
                    ))}
                  </div>
                )}
              </Card>
            )}

            <Button
              onClick={handleFileUpload}
              disabled={!file || uploading}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
              data-testid="submit-upload-button"
            >
              {uploading ? "Uploading..." : "Upload Members"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Social Media Edit Dialog */}
      <Dialog open={showSocialDialog} onOpenChange={setShowSocialDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Social Media Links</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              Add your team's social media handles or full URLs
            </p>
            
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Instagram className="w-4 h-4 text-pink-600" />
                Instagram
              </label>
              <Input
                placeholder="username or https://instagram.com/username"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Facebook className="w-4 h-4 text-blue-400" />
                Facebook
              </label>
              <Input
                placeholder="username or https://facebook.com/username"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Twitter className="w-4 h-4 text-sky-500" />
                Twitter
              </label>
              <Input
                placeholder="username or https://twitter.com/username"
                value={twitter}
                onChange={(e) => setTwitter(e.target.value)}
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => setShowSocialDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveSocialMedia}
                disabled={savingSocial}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {savingSocial ? "Saving..." : "Save Links"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Captain Assignment Dialog */}
      <Dialog open={showCaptainDialog} onOpenChange={setShowCaptainDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Team Captain / Vice Captain</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Role</label>
              <Select value={captainRole} onValueChange={setCaptainRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team_captain">Team Captain</SelectItem>
                  <SelectItem value="vice_captain">Vice Captain</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Search & Select Member</label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={captainSearchTerm}
                  onChange={(e) => setCaptainSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="border rounded-md max-h-48 overflow-y-auto">
                {allMembers
                  .filter(m => m.id !== team?.captain_id && m.id !== team?.vice_captain_id)
                  .filter(m => 
                    !captainSearchTerm || 
                    m.name?.toLowerCase().includes(captainSearchTerm.toLowerCase()) ||
                    m.email?.toLowerCase().includes(captainSearchTerm.toLowerCase()) ||
                    m.membership_number?.toLowerCase().includes(captainSearchTerm.toLowerCase())
                  )
                  .slice(0, 20)
                  .map(member => (
                    <div
                      key={member.id}
                      onClick={() => setSelectedCaptainId(member.id)}
                      className={`p-2 cursor-pointer hover:bg-slate-800/50 border-b last:border-b-0 ${
                        selectedCaptainId === member.id ? 'bg-yellow-500/20 border-yellow-500/30' : ''
                      }`}
                    >
                      <div className="font-medium text-sm">{member.name}</div>
                      <div className="text-xs text-slate-400">{member.email}</div>
                      {member.membership_number && (
                        <div className="text-xs text-slate-400">#{member.membership_number}</div>
                      )}
                    </div>
                  ))}
                {allMembers.filter(m => 
                  m.id !== team?.captain_id && m.id !== team?.vice_captain_id &&
                  (!captainSearchTerm || 
                    m.name?.toLowerCase().includes(captainSearchTerm.toLowerCase()) ||
                    m.email?.toLowerCase().includes(captainSearchTerm.toLowerCase()))
                ).length === 0 && (
                  <div className="p-3 text-center text-slate-400 text-sm">No members found</div>
                )}
              </div>
              {selectedCaptainId && (
                <div className="mt-2 p-2 bg-yellow-500/10 rounded text-sm">
                  Selected: {allMembers.find(m => m.id === selectedCaptainId)?.name}
                </div>
              )}
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => { setShowCaptainDialog(false); setCaptainSearchTerm(""); setSelectedCaptainId(""); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={assignCaptain}
                disabled={assigningCaptain || !selectedCaptainId}
                className="flex-1 bg-yellow-500/100 hover:bg-yellow-400 text-black"
              >
                {assigningCaptain ? "Assigning..." : "Assign"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Player to Team Dialog */}
      <Dialog open={showAddPlayerDialog} onOpenChange={setShowAddPlayerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Player to Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Team Role</label>
              <Select value={teamRole} onValueChange={setTeamRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">1st Team (Primary)</SelectItem>
                  <SelectItem value="secondary">2nd Team (Secondary)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">
                1st team players get main squad priority when team priority signup is enabled
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Search & Select Player</label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, or membership #..."
                  value={playerSearchTerm}
                  onChange={(e) => setPlayerSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="border rounded-md max-h-48 overflow-y-auto">
                {allMembers
                  .filter(m => 
                    !playerSearchTerm || 
                    m.name?.toLowerCase().includes(playerSearchTerm.toLowerCase()) ||
                    m.email?.toLowerCase().includes(playerSearchTerm.toLowerCase()) ||
                    m.membership_number?.toLowerCase().includes(playerSearchTerm.toLowerCase())
                  )
                  .slice(0, 20)
                  .map(member => (
                    <div
                      key={member.id}
                      onClick={() => setSelectedPlayerId(member.id)}
                      className={`p-2 cursor-pointer hover:bg-slate-800/50 border-b last:border-b-0 ${
                        selectedPlayerId === member.id ? 'bg-emerald-500/20 border-emerald-500/30' : ''
                      }`}
                    >
                      <div className="font-medium text-sm">{member.name}</div>
                      <div className="text-xs text-slate-400">{member.email}</div>
                      {member.membership_number && (
                        <div className="text-xs text-slate-400">#{member.membership_number}</div>
                      )}
                      <div className="text-xs text-blue-500">
                        {member.primary_team ? `1st: ${member.primary_team}` : 'No 1st team'}
                        {member.second_team && ` | 2nd: ${member.second_team}`}
                      </div>
                    </div>
                  ))}
                {allMembers.filter(m => 
                  !playerSearchTerm || 
                  m.name?.toLowerCase().includes(playerSearchTerm.toLowerCase()) ||
                  m.email?.toLowerCase().includes(playerSearchTerm.toLowerCase())
                ).length === 0 && (
                  <div className="p-3 text-center text-slate-400 text-sm">No members found</div>
                )}
              </div>
              {selectedPlayerId && (
                <div className="mt-2 p-2 bg-emerald-500/10 rounded text-sm">
                  Selected: {allMembers.find(m => m.id === selectedPlayerId)?.name}
                </div>
              )}
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => { setShowAddPlayerDialog(false); setPlayerSearchTerm(""); setSelectedPlayerId(""); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={addPlayerToTeam}
                disabled={addingPlayer || !selectedPlayerId}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {addingPlayer ? "Adding..." : "Add to Team"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Assignment Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={(open) => { 
        setShowRoleDialog(open); 
        if (!open) { setRoleSearchTerm(""); setSelectedRoleUserId(""); }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Team Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Role Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">Select Role</label>
              <Select value={selectedRoleType} onValueChange={setSelectedRoleType}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue placeholder="Choose a role..." />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1 text-xs text-slate-400 font-semibold">Standard Roles</div>
                  {STANDARD_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                  {customRoles.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs text-slate-400 font-semibold mt-2">Custom Roles</div>
                      {customRoles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Member Search */}
            <div>
              <label className="text-sm font-medium mb-2 block">Select Member</label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search members..."
                  value={roleSearchTerm}
                  onChange={(e) => setRoleSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="role-member-search"
                />
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-lg border-slate-700">
                {allMembers
                  .filter(m => 
                    !roleSearchTerm || 
                    m.name?.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
                    m.email?.toLowerCase().includes(roleSearchTerm.toLowerCase())
                  )
                  .map((member) => (
                    <div
                      key={member.id}
                      className={`p-2 hover:bg-slate-800 cursor-pointer border-b border-slate-700 last:border-b-0 ${
                        selectedRoleUserId === member.id ? 'bg-yellow-500/20' : ''
                      }`}
                      onClick={() => setSelectedRoleUserId(member.id)}
                    >
                      <div className="font-medium text-white text-sm">{member.name}</div>
                      <div className="text-xs text-slate-400">{member.email}</div>
                    </div>
                  ))}
                {allMembers.filter(m => 
                  !roleSearchTerm || 
                  m.name?.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
                  m.email?.toLowerCase().includes(roleSearchTerm.toLowerCase())
                ).length === 0 && (
                  <div className="p-3 text-center text-slate-400 text-sm">No members found</div>
                )}
              </div>
              {selectedRoleUserId && (
                <div className="mt-2 p-2 bg-yellow-500/10 rounded text-sm">
                  Selected: {allMembers.find(m => m.id === selectedRoleUserId)?.name}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => { setShowRoleDialog(false); setRoleSearchTerm(""); setSelectedRoleUserId(""); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignRole}
                disabled={assigningRole || !selectedRoleUserId || !selectedRoleType}
                className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black"
              >
                {assigningRole ? "Assigning..." : "Assign Role"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Broadcast to Team Dialog */}
      <Dialog open={showBroadcastDialog} onOpenChange={setShowBroadcastDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-400">
              <MessageSquare className="w-5 h-5" />
              Broadcast to {team?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-300">Message *</label>
              <Textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Type your message to all team members..."
                className="mt-1"
                rows={4}
              />
              <p className="text-xs text-slate-500 mt-1">
                {broadcastMessage.length}/500 characters
              </p>
            </div>
            
            <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <p className="text-sm text-purple-400">
                <Send className="w-4 h-4 inline mr-2" />
                This message will be sent via Telegram to all team members with linked accounts
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowBroadcastDialog(false);
                  setBroadcastMessage("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={sendTeamBroadcast}
                disabled={broadcasting || !broadcastMessage.trim()}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                {broadcasting ? "Sending..." : "Send Broadcast"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ActiveSquadSheet
        open={showActiveSheet}
        teamId={teamId}
        teamName={team?.name || "this team"}
        onClose={() => setShowActiveSheet(false)}
      />
    </div>
  );
}
